import styled from "styled-components";
import { Card } from "@mui/material";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  FONTS,
} from "../../../../../utils/constants";

export const HeroCard = styled(Card)<{ $isDeactivated: boolean }>`
  && {
    position: relative;
    border-radius: 12px;
    box-shadow: var(--shadow-card-rest);
    border: 1px solid var(--color-card-border);
    background: ${({ $isDeactivated }) =>
      $isDeactivated
        ? "var(--color-card-background-deactivated)"
        : "var(--hero-background)"};

    font-family: ${FONTS.serif};
    padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;

    @media ${QUERIES.tabletLargeAndUp} {
      padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
    }

    @media ${QUERIES.laptopAndUp} {
      padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
    }
  }
`;

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 1.4fr 1fr;
    align-items: start;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
  padding-right: ${SPACING.xl + SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding-right: 0;
  }
`;

export const HeroName = styled.h1`
  font-family: ${FONTS.serif};
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-default);
  margin: 0;
`;

export const HeroStatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-family: ${FONTS.serif};
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: ${({ $isActive }) =>
    $isActive
      ? "color-mix(in srgb, var(--color-success) 14%, transparent)"
      : "color-mix(in srgb, var(--color-text-muted) 14%, transparent)"};
  color: ${({ $isActive }) =>
    $isActive ? "var(--color-success)" : "var(--color-text-muted)"};

  &::before {
    content: "";
    width: 0.4em;
    height: 0.4em;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const HeroModeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border: 1px solid var(--color-card-border);
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-text-muted) 8%, transparent);
  color: var(--color-text-secondary);
  font-family: ${FONTS.serif};
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const HeroSubtitle = styled.p`
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin: 0;
`;

export const ValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  margin-top: ${SPACING.md}px;
`;

export const ValueLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const ValueAmount = styled.span<{ $negative: boolean }>`
  font-family: ${FONTS.serif};
  font-size: var(--text-display-xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-text-default)"};
`;

export const GainRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.xs}px;
`;

export const GainChip = styled.span<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  font-family: ${FONTS.serif};
  font-size: var(--text-md);
  font-weight: 700;
  color: ${({ $negative }) =>
    $negative ? "var(--color-error)" : "var(--color-success)"};

  svg {
    font-size: var(--text-lg);
  }
`;

export const MetricsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  margin-top: ${SPACING.lg}px;
  background: var(--color-card-background-transparent);
  border: 1px solid var(--color-card-border);
  border-radius: 10px;
  padding: ${SPACING.md}px;
  box-shadow: var(--shadow-card-rest);

  @media ${QUERIES.tabletLargeAndUp} {
    background: transparent;
    border: none;
    border-top: 1px solid var(--color-border);
    border-radius: 0;
    padding: ${SPACING.lg}px 0 0;
    box-shadow: none;
  }
`;

export const MetricsGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: start;
  gap: ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${SPACING.lg}px;
  }
`;

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;

  @media not all and ${QUERIES.tabletLargeAndUp} {
    border-bottom: 1px solid var(--color-border);
    padding-bottom: ${SPACING.sm}px;
  }
`;

export const MetricLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const MetricValue = styled.span`
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-md);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

export const ActionsRow = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    gap: ${SPACING.sm}px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`;

export const KebabWrapper = styled.div`
  position: absolute;
  top: ${SPACING.md}px;
  right: ${SPACING.md}px;
  z-index: 10;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const DesktopComposition = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: block;
  }
`;

export const MobileComposition = styled.div`
  margin-top: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;
